import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';
import toast from 'react-hot-toast';

const C = { or: '#B8960A', noir: '#1A1710', border: '#E8D8A8', orBg: '#FEF9EC' };
const IS = { width: '100%', padding: '10px 14px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' as const, fontFamily: 'inherit' };

const schema = z.object({
  email:        z.string().email('Email invalide'),
  password:     z.string().min(8, 'Minimum 8 caractères'),
  first_name:   z.string().min(1, 'Prénom requis'),
  last_name:    z.string().min(1, 'Nom requis'),
  company_name: z.string().min(1, 'Raison sociale requise'),
  phone:        z.string().optional(),
});

type FormData = z.infer<typeof schema>;

export default function RegisterPage() {
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<FormData>({ resolver: zodResolver(schema) });

  const onSubmit = async (data: FormData) => {
    try {
      const res = await api.post('/auth/register', data);
      const { user, accessToken, refreshToken } = res.data.data;
      setAuth(user, accessToken, refreshToken);
      navigate('/app/client/dashboard');
      toast.success('Bienvenue sur VAEG Conformite !');
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Erreur lors de la création du compte');
    }
  };

  const Field = ({ name, label, type = 'text', placeholder }: { name: keyof FormData; label: string; type?: string; placeholder?: string }) => (
    <div>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>{label}</label>
      <input {...register(name)} type={type} placeholder={placeholder} style={IS} />
      {errors[name] && <p style={{ fontSize: 12, color: '#dc2626', marginTop: 4 }}>{errors[name]?.message}</p>}
    </div>
  );

  return (
    <div style={{ minHeight: '100dvh', background: `linear-gradient(135deg, #F9F1E7 0%, #F2E4CC 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 20, boxShadow: '0 8px 40px rgba(26,23,16,0.12)', width: '100%', maxWidth: 440, padding: '40px 36px' }}>
        <div style={{ height: 48, backgroundImage: 'url(/logo_vaeg_assist.png)', backgroundSize: 'contain', backgroundPosition: 'center left', backgroundRepeat: 'no-repeat', marginBottom: 8 }} />
        <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 28 }}>Créez votre espace entreprise</p>

        <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="workspace-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field name="first_name" label="Prénom" />
            <Field name="last_name" label="Nom" />
          </div>
          <Field name="company_name" label="Raison sociale" placeholder="Ma Société SAS" />
          <Field name="email" label="Email professionnel" type="email" placeholder="vous@entreprise.com" />
          <Field name="phone" label="Téléphone" type="tel" placeholder="+33 6 00 00 00 00" />
          <Field name="password" label="Mot de passe" type="password" />

          <button type="submit" disabled={isSubmitting} style={{ height: 44, background: C.or, color: '#fff', border: 'none', borderRadius: 9, fontSize: 14, fontWeight: 600, cursor: isSubmitting ? 'not-allowed' : 'pointer', opacity: isSubmitting ? 0.7 : 1, marginTop: 4 }}>
            {isSubmitting ? 'Création du compte...' : 'Créer mon compte'}
          </button>
        </form>

        <p style={{ textAlign: 'center', fontSize: 13, color: '#6b7280', marginTop: 20 }}>
          Déjà un compte ?{' '}
          <Link to="/login" style={{ color: C.or, fontWeight: 600, textDecoration: 'none' }}>Se connecter</Link>
        </p>
      </div>
    </div>
  );
}
